import type { UploadFile } from '@edwin-group/cj-graphql/generated/graphql';
import { ImageSize } from './useResponsiveImage';

/**
 * Check if a given URL exists (returns 2xx range for HEAD request).
 */
async function checkImageExists(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok; // true if 200-299
  } catch {
    return false;
  }
}

/**
 * Returns an object with `src` and `alt`.
 * If the "resized" URL (e.g. `medium_...`) does not exist, fallback to the original URL.
 *
 * @param image
 * @param size    - ImageSize
 *        medium: 1920,
 *        small: 1000,
 *        thumbnail: 500,
 * @returns
 */
export async function getImageProps(
  image:
    | UploadFile
    | {
        url: string;
        alternativeText: string;
      }
    | undefined,
  size: ImageSize,
): Promise<{
  src?: string;
  alt?: string;
}> {
  if (!image) {
    return {
      src: undefined,
      alt: undefined,
    };
  }

  // Extract URL & alt text regardless of data shape
  const url = 'url' in image ? image.url : (image as UploadFile)?.data?.attributes?.url;

  const alt = 'url' in image ? image.alternativeText : (image as UploadFile)?.data?.attributes?.alternativeText;

  // Default to original URL if size is "original"
  if (!url || size === 'original') {
    return {
      src: url,
      alt: alt || '',
    };
  }

  // Construct the resized URL
  const urlParts = url.split('/');
  const imageIndex = (urlParts.length || 1) - 1;
  const fileName = urlParts[imageIndex];

  // If the file already has the size in its name, just return it
  if (fileName.includes(size)) {
    return {
      src: url,
      alt: alt || '',
    };
  }

  // Otherwise, build a new one (e.g. `.../medium_originalName.jpg`)
  const resizedURL = [...urlParts.slice(0, imageIndex), `${size}_${fileName}`].join('/');

  // Check if the resized URL exists
  const resizedExists = await checkImageExists(resizedURL);

  // If it exists, use it; if not, use the original
  return {
    src: resizedExists ? resizedURL : url,
    alt: alt || '',
  };
}
